/**
 * The single source of truth for rendering timestamps in the GUI.
 *
 * THUB-272 / THUB-186. Before this module there were 55 ad-hoc `toLocaleString` /
 * `Intl.DateTimeFormat` calls across 27 files and SIX duplicate copies of
 * `formatRelativeTime`, each implicitly rendering in whatever timezone the viewer's laptop
 * happened to be set to. Two users looking at the same transfer saw different times, with
 * no way to standardize.
 *
 * ## The bug this fixes
 *
 * The backend used to emit timestamps with NO timezone designator (`2026-07-30T20:12:21`).
 * Per ECMAScript, a date-time string without an offset is parsed as **local** time — so a
 * US-Central browser read every UTC timestamp as 5 hours in the FUTURE. Every relative-age
 * calculation came out negative, every `if (minutes < 1) return 'Just now'` branch matched,
 * and the entire Audit History / Recent Activity feed collapsed to "Just now".
 *
 * The backend now emits canonical RFC 3339 (`...+00:00`), but `parseUtc` below still treats
 * a missing designator as UTC. That is deliberate and load-bearing: roughly 5,100 rows
 * already exist in the tables with the legacy naive format, and deploying code does not
 * migrate stored data. Those rows must keep rendering correctly for as long as they live.
 *
 * ## Timezone policy
 *
 * Instants are UTC end to end; the timezone is a *display* concern and lives only here.
 * Callers pass the active IANA zone (from `useTimezone`), defaulting to America/Chicago.
 */

/** The company default when a user has expressed no preference. */
export const DEFAULT_TIMEZONE = 'America/Chicago';

/** Where TimezoneContext persists the user's choice. Shared so the non-React read below
 *  cannot drift from the provider. */
export const TIMEZONE_STORAGE_KEY = 'transferhub.timezone';

/**
 * The active display timezone, readable from OUTSIDE React.
 *
 * `useTimezone()` is the right entry point inside a component. This exists for the handful of
 * modules that format timestamps from a plain function — called out of several sub-components,
 * or inside a `.map` callback — where a hook cannot legally be used.
 *
 * It is backed by a module-level cache the provider keeps in sync via
 * {@link setActiveTimezone}, seeded from localStorage on first read. The cache is what makes
 * the selector actually work for those modules: reading localStorage on every call returned
 * the right VALUE but produced no re-render, so timestamps kept rendering in the previous zone
 * until a full reload. Components that use these helpers still need to re-render for the new
 * value to appear, which is why the provider ALSO bumps its context — the cache and the
 * context change together.
 */
let activeZoneCache: string | null = null;

export function activeTimezone(): string {
  if (activeZoneCache) return activeZoneCache;
  try {
    const stored = window.localStorage.getItem(TIMEZONE_STORAGE_KEY);
    if (stored) {
      // Validate before caching: a stale or hand-edited value must not throw inside
      // Intl.DateTimeFormat on every render.
      new Intl.DateTimeFormat('en-US', { timeZone: stored });
      activeZoneCache = stored;
      return stored;
    }
  } catch {
    /* unavailable or invalid -> company default */
  }
  activeZoneCache = DEFAULT_TIMEZONE;
  return DEFAULT_TIMEZONE;
}

/**
 * Point the non-React readers at a new zone. Called by TimezoneProvider so the plain-function
 * formatters and the hook can never disagree about which zone is active.
 */
export function setActiveTimezone(tz: string): void {
  activeZoneCache = tz;
}

/**
 * Parse a backend timestamp into a `Date`, treating a missing offset as UTC.
 *
 * Returns `null` rather than an `Invalid Date` for unusable input, so callers can render a
 * placeholder instead of the string "Invalid Date" leaking into the UI.
 */
export function parseUtc(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const fromEpoch = new Date(value);
    return Number.isNaN(fromEpoch.getTime()) ? null : fromEpoch;
  }

  let text = String(value).trim();
  if (!text) return null;

  // Does it already carry a designator? `Z`, `+HH:MM` or `-HH:MM` at the very end.
  // The offset test deliberately looks past the date part, so the '-' in '2026-07-30'
  // is not mistaken for a negative offset.
  const hasZulu = /[Zz]$/.test(text);
  const hasOffset = /[+-]\d{2}:?\d{2}$/.test(text.slice(10));

  if (!hasZulu && !hasOffset) {
    // Legacy naive value. Every naive timestamp this system wrote was UTC — say so
    // explicitly, otherwise the browser assumes local time and future-dates it.
    text = `${text}Z`;
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const DATE_STYLES: Record<string, Intl.DateTimeFormatOptions> = {
  // 'Jul 30, 2026, 08:12 PM CDT' — the default for most surfaces.
  full: {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
  },
  // Same, to the second, for audit/journey rows where ordering matters.
  precise: {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short',
  },
  // 'Jul 30, 2026'
  dateOnly: { year: 'numeric', month: 'short', day: 'numeric' },
  // '08:12 PM CDT'
  timeOnly: { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' },
};

export type DateStyle = keyof typeof DATE_STYLES;

/**
 * Format a backend timestamp in the given IANA timezone.
 *
 * `timeZoneName: 'short'` is included on the default styles on purpose: an unlabelled
 * wall-clock time is exactly the ambiguity that made this class of bug invisible for four
 * months. Showing "CDT" makes a wrong zone self-evident instead of plausible.
 */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  timeZone: string = DEFAULT_TIMEZONE,
  style: DateStyle = 'full',
  fallback = '—',
): string {
  const parsed = parseUtc(value);
  if (!parsed) return fallback;
  try {
    return new Intl.DateTimeFormat('en-US', { ...DATE_STYLES[style], timeZone }).format(parsed);
  } catch {
    // An invalid IANA zone (e.g. a stale localStorage value) must not blank the whole
    // page — fall back to the company default rather than throwing inside render.
    return new Intl.DateTimeFormat('en-US', {
      ...DATE_STYLES[style], timeZone: DEFAULT_TIMEZONE,
    }).format(parsed);
  }
}

/**
 * Human-readable age, e.g. "just now", "5m ago", "3h ago", "2d ago", then an absolute date.
 *
 * Replaces six near-identical copies of this logic. Unlike those, it handles the
 * FUTURE-timestamp case explicitly: pre-THUB-272 a negative difference silently fell into
 * the `< 1 minute` branch and rendered "Just now", which is precisely how the timezone bug
 * disguised itself as normal output.
 */
export function formatRelativeTime(
  value: string | number | Date | null | undefined,
  timeZone: string = DEFAULT_TIMEZONE,
  fallback = '—',
): string {
  const parsed = parseUtc(value);
  if (!parsed) return fallback;

  const diffMs = Date.now() - parsed.getTime();

  // Genuinely in the future (clock skew, or a scheduled next-run time). Say so rather than
  // reporting it as "just now"; more than a minute of skew is worth surfacing.
  if (diffMs < -60_000) {
    return `in ${describeSpan(-diffMs)}`;
  }

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(diffMs / 86_400_000);
  if (days < 7) return `${days}d ago`;

  // Beyond a week a relative age stops being useful; show the actual date.
  return formatDateTime(parsed, timeZone, 'dateOnly', fallback);
}

function describeSpan(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${Math.max(minutes, 1)}m`;
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(ms / 86_400_000)}d`;
}

/** The abbreviation for a zone right now (e.g. "CDT"), for labelling a selector. */
export function timezoneAbbreviation(timeZone: string = DEFAULT_TIMEZONE): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone, timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/** Every IANA zone the runtime knows, for the selector. Falls back to a curated list. */
export function supportedTimezones(): string[] {
  const anyIntl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  if (typeof anyIntl.supportedValuesOf === 'function') {
    try {
      return anyIntl.supportedValuesOf('timeZone');
    } catch {
      /* fall through */
    }
  }
  return [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Anchorage', 'Pacific/Honolulu', 'UTC', 'Europe/London', 'Europe/Dublin',
    'Europe/Paris', 'Europe/Berlin', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo',
    'Australia/Sydney',
  ];
}
